import { ErrorCode } from '@/api/types/pt_enums'
import { PtError } from '@/types/pt_types'

import type { LoginInfo } from '@/api/types/pt_entities'

interface RefreshSubscriberType {
  resolve: (token: string) => void
  reject: (reason?: PtError) => void
}
let refreshTimer: ReturnType<typeof setTimeout>
let isRefreshing = false
let refreshSubscribers: RefreshSubscriberType[] = []
export const hideTabBar = ref(false)
export const firstUrl = ref('')
export const HOME_PAGE = '/index'
export const LOGIN_PAGE = '/login'

export function getLoginInfo(): LoginInfo {
  return JSON.parse(localStorage.getItem('AUTH_TOKEN') || '{}')
}

export function setLoginInfo(loginInfo?: LoginInfo): void {
  if (loginInfo) {
    localStorage.setItem('AUTH_TOKEN', JSON.stringify(loginInfo))
  } else {
    localStorage.removeItem('AUTH_TOKEN')
  }
}

export function isLoginExpired(loginInfo?: LoginInfo): boolean {
  if (!loginInfo || !loginInfo.accessToken) return true
  return Date.now() >= (loginInfo.accessExpireTime || 0)
}

export function isRefreshExpired(loginInfo?: LoginInfo): boolean {
  if (!loginInfo || !loginInfo.refreshToken) return true
  return Date.now() >= (loginInfo.refreshExpireTime || 0)
}

export function subscribeTokenRefresh(resolve: (token: string) => void, reject: (reason?: PtError) => void) {
  refreshSubscribers.push({ resolve, reject })
}

export function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach((subscriber) => subscriber.resolve(newToken))
  refreshSubscribers = []
}

export function onTokenError(error: PtError) {
  refreshSubscribers.forEach((subscriber) => subscriber.reject(error))
  refreshSubscribers = []
}

export function startRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
  }
  const loginInfo = getLoginInfo()
  if (isLoginExpired(loginInfo) || isRefreshExpired(loginInfo)) {
    return
  }
  const now = Date.now()
  const delay = Math.max(loginInfo.accessExpireTime! - now - 60 * 1000, 0) // 提前1分钟刷新
  refreshTimer = setTimeout(() => {
    getAccessToken(false, false).catch(() => {
      setLoginInfo(undefined)
    })
  }, delay)
}

export function stopRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
  }
}

/**
 * force=true 表示强制要得到一个访问令牌，得不到强报错
 */
export async function getAccessToken(force?: boolean, redirectToLogin: boolean = true) {
  const loginInfo = getLoginInfo()
  if (!force && !isLoginExpired(loginInfo)) {
    // accessToken有效 且 不强制刷新 则直接返回
    return loginInfo.accessToken
  }
  if (isRefreshExpired(loginInfo)) {
    // 刷新令牌已过期，如果不强制则返回空，强制则返回没登录异常
    if (force) {
      throw new PtError(ErrorCode.NOT_LOGIN.value, ErrorCode.NOT_LOGIN.label)
    } else {
      return ''
    }
  }
  if (isRefreshing) {
    // 正在刷新中，返回一个等待Promise
    return new Promise<string>((resolve, reject) => {
      subscribeTokenRefresh(resolve, reject)
    })
  }
  isRefreshing = true
  try {
    const newLoginInfo = await apiRefreshToken({
      refreshToken: loginInfo.refreshToken,
      options: { redirectToLogin, silent: true }
    })
    setLoginInfo(newLoginInfo)
    startRefreshTimer() // 重启定时刷新
    onTokenRefreshed(newLoginInfo.accessToken!)
    return newLoginInfo.accessToken
  } catch (error: any) {
    if (force) {
      onTokenError(error)
      if (error.code === ErrorCode.NOT_LOGIN.value) {
        // 跳转到登录页
      }
    } else {
      onTokenRefreshed('')
    }
  } finally {
    isRefreshing = false
  }
}

export function checkUserAgent(patterns: string | string[]) {
  const ua = navigator.userAgent
  const list = Array.isArray(patterns) ? patterns : [patterns]
  for (let i = 0; i < list.length; i++) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (ua.match(new RegExp(list[i], 'i'))?.toString().toLowerCase() === list[i].toLowerCase()) {
      return true
    }
  }
  return false
}
